import { useEffect } from "react";
import { useT } from "@/i18n/LocaleContext";

const SITE = "https://www.hamm.gmbh"; // Canonical Domain (Suchmaschinen-Signal)

function setMeta(attr, value, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${value}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href, hreflang) {
  const key = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(key);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * SEOHead – setzt Title, Description, Canonical, hreflang, Open Graph und Twitter Card
 * dynamisch je nach Route/Sprache. Ausserdem wird das Organization/LocalBusiness JSON-LD
 * einmalig in den Head geschrieben.
 */
export default function SEOHead({ path = "/", ogImage = "/assets/hero-laser.jpg" }) {
  const { locale, t } = useT();

  useEffect(() => {
    const title = t("meta.title");
    const desc = t("meta.description");
    const keywords = t("meta.keywords");
    const ogAlt = t("meta.ogAlt");

    document.title = title;
    document.documentElement.setAttribute("lang", locale);

    setMeta("name", "description", desc);
    setMeta("name", "keywords", keywords);
    setMeta("name", "robots", "index, follow, max-image-preview:large");

    // Canonical + hreflang (DE default, EN unter /en)
    const cleanPath = path === "/" ? "" : path;
    const dePath = `${SITE}${cleanPath || "/"}`;
    const enPath = `${SITE}/en${cleanPath}`;
    const canonicalPath = locale === "en" ? enPath : dePath;
    setLink("canonical", canonicalPath);
    setLink("alternate", dePath, "de");
    setLink("alternate", enPath, "en");
    setLink("alternate", dePath, "x-default");

    // Open Graph
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "Hamm Entwicklung + Produktion GmbH");
    setMeta("property", "og:locale", locale === "en" ? "en_US" : "de_DE");
    setMeta("property", "og:locale:alternate", locale === "en" ? "de_DE" : "en_US");
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", canonicalPath);
    setMeta("property", "og:image", `${SITE}${ogImage}`);
    setMeta("property", "og:image:alt", ogAlt);
    setMeta("property", "og:image:width", "1600");
    setMeta("property", "og:image:height", "750");

    // Twitter Card
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", `${SITE}${ogImage}`);
    setMeta("name", "twitter:image:alt", ogAlt);
  }, [locale, path, ogImage, t]);

  return null;
}
